import FeatureFlagWebClient, {
  EnvironmentType,
  Identifiers,
  SupportedFlagTypes,
} from '@atlassiansox/feature-flag-web-client';
import { GetValueOptions, RawFlags } from '@atlassiansox/feature-flag-web-client/src/api/types';

import { getConfig, getEnv } from '../config';
import ProductStore from '../store/productStore';
import UserStore from '../store/userStore';
import measurePerformance, { PerformanceMetric } from '../util/measurePerformance';

import AnalyticsClient from './AnalyticsClient';

export type FeatureFlagEnum =
  | 'uchi.enable-nav-v3'
  | 'redirect-start-traffic-to-atlas_q1io5'
  | 'post_office_in_uchi_switcher';

interface FlagValueTypeMapping {
  ['uchi.enable-nav-v3']: SupportedFlagTypes;
  ['redirect-start-traffic-to-atlas_q1io5']: SupportedFlagTypes;
  ['post_office_in_uchi_switcher']: SupportedFlagTypes;
}

export type FeatureFlagValues = { [s in FeatureFlagEnum]: FlagValueTypeMapping[s] };

const { fx3ApiKey } = getConfig();
const ENV_MAP: Record<Env, EnvironmentType> = {
  test: EnvironmentType.LOCAL,
  local: EnvironmentType.LOCAL,
  ddev: EnvironmentType.DEV,
  'stg-east': EnvironmentType.STAGING,
  'stg-apse': EnvironmentType.STAGING,
  'stg-fedm-east': EnvironmentType.STAGING,
  'prod-east': EnvironmentType.PROD,
  'prod-fedm-east': EnvironmentType.PROD,
};
const FF_ENV = ENV_MAP[getEnv()];
const PRODUCT_KEY = 'clementine';
const ATLASSIAN_CLOUD_IDS = new Set([
  'a436116f-02ce-4520-8fbb-7301462a1674', // hello
  'DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5', // pug
]);
const IS_ATLASSIAN_USER_KEY = 'isAtlassianUser';

export default class FeatureFlagsClient {
  private analyticsClient: AnalyticsClient;
  private fx3Client: FeatureFlagWebClient | undefined;
  private productStore: ProductStore;
  private userStore: UserStore;

  constructor(userStore: UserStore, productStore: ProductStore, analyticsClient: AnalyticsClient) {
    this.userStore = userStore;
    this.productStore = productStore;
    this.analyticsClient = analyticsClient;
  }

  getFlags(): RawFlags {
    return this.fx3Client ? this.fx3Client.getFlags() : {};
  }

  getFlagValue<T extends SupportedFlagTypes>(flagKey: string, defaultValue: T, options?: GetValueOptions<T>): T {
    return this.fx3Client ? this.fx3Client.getFlagValue(flagKey, defaultValue, options) : defaultValue;
  }

  async initialiseClient() {
    // This could be called from constructor when we're ready to replace Worklens feature flags
    if (!this.fx3Client) {
      const accountId = await this.userStore.readyPromise;

      const client = new FeatureFlagWebClient(
        fx3ApiKey,
        this.analyticsClient.getAnalyticsWebClient(),
        {
          identifier: {
            type: Identifiers.ATLASSIAN_ACCOUNT_ID,
            value: accountId,
          },
          custom: {
            [IS_ATLASSIAN_USER_KEY]: await this.isAtlassianUser(),
          },
        },
        {
          productKey: PRODUCT_KEY,
          environment: FF_ENV,
        },
      );

      await measurePerformance(PerformanceMetric.FeatureFlagClient, () => client.ready());
      this.fx3Client = client;
      this.analyticsClient.track('featureFlagsWebClient', 'initialised');
    }
  }

  private async isAtlassianUser(): Promise<boolean> {
    const { sites } = await this.productStore.getAvailableSites();
    const availableCloudIds = sites.map(site => site.cloudId);
    return availableCloudIds.some(cloudId => ATLASSIAN_CLOUD_IDS.has(cloudId));
  }
}
